import { Box, Chip, Paper, Stack, Typography, useTheme } from "@mui/material";
import { Elements } from "@stripe/react-stripe-js";
import DefaultCard from "assets/images/defaultCard.svg";
import MastercardSvg from "assets/images/mastercard.svg";
import VisaSvg from "assets/images/visa.svg";
import {
  Button,
  CalendarIcon,
  CircularLoader,
  ConfirmationBanner,
  CreditCardIcon,
  DollarCashIcon,
  HeadphoneIcon,
  LocationPinIcon,
  SeatIcon,
} from "components";
import {
  useDefaultCard,
  useGetActiveResourceCount,
  useGetAllCountries,
  useGetAllStateByCountryId,
  useGetCards,
  useGetSetupIntent,
  useGlobalStore,
  useSubscriptionDetails,
} from "controller";
import { useCallback, useMemo, useState } from "react";
import toast from "react-hot-toast";
import type { Country, State, SubscriptionCard } from "shared";
import { dayjs } from "utilz";
import { convertStringToSentenceCase } from "utilz/helpers";
import stripePromise from "utilz/stripe";
import { AddPaymentMethod } from "./AddPaymentMethod";
import { CancelSubscriptionFeedbackDrawer } from "./cancel-subsription-feedback";
import {
  AddressEditDrawer,
  CardListDrawer,
  InvoicesDrawer,
  ManageSeatsDrawer,
  ManageTechDrawer,
} from "./drawers";

export const OverviewTab04 = () => {
  const theme = useTheme();

  // Modal/Drawer states
  const [isManageSeatsDrawerOpen, setIsManageSeatsDrawerOpen] = useState(false);
  const [isManageTechDrawerOpen, setIsManageTechDrawerOpen] = useState(false);
  const [isAddressEditDrawerOpen, setIsAddressEditDrawerOpen] = useState(false);
  const [isAddCardsOpen, setIsAddCardsOpen] = useState(false);
  const [isCardListDrawerOpen, setIsCardListDrawerOpen] = useState(false);
  const [isInvoicesDrawerOpen, setIsInvoicesDrawerOpen] = useState(false);
  const [isCancelSubscriptionModalOpen, setIsCancelSubscriptionModalOpen] =
    useState(false);
  const [setupIntentId, setSetupIntentId] = useState<string>();

  // Global store
  const currentAccessBlockDate = useGlobalStore(
    (state) => state.currentAccessBlockDate,
  );
  const dateFormat = useGlobalStore((state) => state.dateFormat);
  const subscriptionStatus = useGlobalStore(
    (state) => state.subscriptionStatus,
  );

  // API hooks
  const {
    data: subscriptionDetails,
    refetch: refetchSubscriptionDetails,
    isLoading: isSubsLoading,
  } = useSubscriptionDetails();
  const { data: activeResourceCount } = useGetActiveResourceCount();
  const { data: countries } = useGetAllCountries();
  const { data: states } = useGetAllStateByCountryId(
    `${subscriptionDetails?.billing_country}` || "",
  );
  const { data: cardList, refetch: refetchCards } = useGetCards();
  const { mutateAsync: setDefaultCard } = useDefaultCard();
  const { mutateAsync: getSetupIntent, isLoading: loadingSetupIntent } =
    useGetSetupIntent();

  // Calculate prices
  const TIERED_BASE_PRICE = 499;
  const TIERED_EXTRA_SEAT_PRICE = 69;
  const TIERED_INCLUDED_SEATS = 5;

  const totalBillingAmount = useMemo(() => {
    if (!subscriptionDetails) return 0;

    const discountMultiplier =
      (100 - Number(subscriptionDetails.percent_off || 0)) / 100;

    if (subscriptionDetails.pricing_type === "tiered") {
      const seatCount = Number(subscriptionDetails.seat_quantity);
      const tieredTotal =
        TIERED_BASE_PRICE +
        Math.max(0, seatCount - TIERED_INCLUDED_SEATS) *
          TIERED_EXTRA_SEAT_PRICE;
      return tieredTotal * discountMultiplier;
    }

    // Flat pricing
    const pricePerSeat = Number(subscriptionDetails.price) / 100;
    return (
      Number(subscriptionDetails.seat_quantity) *
      pricePerSeat *
      discountMultiplier
    );
  }, [subscriptionDetails]);

  // Get country and state names for display
  const getCountryName = () => {
    if (!(countries && subscriptionDetails?.billing_country)) return "";
    const country = countries.find(
      (c: Country) => c.id === subscriptionDetails.billing_country,
    );
    return country?.name || "";
  };

  const getStateName = () => {
    if (!(states && subscriptionDetails?.billing_state)) return "";
    const state = states.find(
      (s: State) => s.id === subscriptionDetails.billing_state,
    );
    return state?.state_name || "";
  };

  const getAddressParts = () => {
    return [
      subscriptionDetails?.line1,
      subscriptionDetails?.line2,
      getStateName(),
      getCountryName(),
      subscriptionDetails?.billing_postal_code,
    ].filter(Boolean);
  };

  const cardIcon = useCallback((type: string) => {
    switch (type) {
      case "visa":
        return VisaSvg;
      case "mastercard":
        return MastercardSvg;
      default:
        return DefaultCard;
    }
  }, []);

  const handleAddCardClick = async () => {
    try {
      const data = await getSetupIntent();
      if (data?.client_secret) setSetupIntentId(data.client_secret);
    } catch (_error) {
      toast.error("Unable to add new card");
    }
    setIsAddCardsOpen((prev) => !prev);
  };

  const handleSetAsDefault = async (id: string) => {
    try {
      await setDefaultCard(id);
      refetchCards();
      refetchSubscriptionDetails();
    } catch (_error) {
      toast.error("Unable to set default card");
    }
  };

  const handleCardUpdated = () => {
    refetchCards();
    refetchSubscriptionDetails();
  };

  if (isSubsLoading) {
    return (
      <Stack
        sx={{
          width: "100%",
          height: "100%",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularLoader />
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        // padding: "32px",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        backgroundColor: theme.palette.neutral["005"],
        borderRadius: "8px",
        overflow: "hidden",
        border: `1px solid ${theme.palette.neutral["015"]}`,
      }}
    >
      {/* Page Header */}
      <Box sx={{ p: "8px 32px", backgroundColor: "#FFF" }}>
        <Typography variant="h4" sx={{ color: theme.palette.lightBg.high }}>
          DeskDay subscription
        </Typography>
      </Box>

      {/* Plan Controls Container */}
      <Stack
        sx={{
          backgroundColor: theme.palette.neutral.main,
          borderTop: `1px solid ${theme.palette.neutral["015"]}`,
          borderBottom: `1px solid ${theme.palette.neutral["015"]}`,
          p: "12px 32px",
        }}
      >
        {/* Plan Title and Actions Row */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" alignItems="center" gap="12px">
            <Typography variant="h6" sx={{ color: theme.palette.lightBg.high }}>
              {convertStringToSentenceCase(subscriptionDetails?.plan_name) ||
                "Ticketing PSA"}
            </Typography>
            <Chip
              label="ACTIVE PLAN"
              size="small"
              sx={{
                ...theme.typography["tag-label-xs"],
                backgroundColor: theme.palette.primitive.system.violet[50],
                color: theme.palette.primitive.system.violet[800],
                borderRadius: "4px",
                height: "20px",
                textTransform: "uppercase",
              }}
            />
          </Stack>
          <Stack direction="row" alignItems="center" gap="16px">
            <Button
              variant="ghost"
              size="small"
              onClick={() => setIsInvoicesDrawerOpen(true)}
            >
              View invoices
            </Button>
          </Stack>
        </Stack>
      </Stack>
      {subscriptionStatus === "past_due" && (
        <ConfirmationBanner
          variant="error"
          description={
            <Typography
              variant="subtitle1"
              color={theme.palette.primitive.system.red[800]}
            >
              Your next payment retry date is on{" "}
              <Typography
                component={"span"}
                variant="subtitle1"
                color={theme.palette.primitive.system.red[800]}
              >
                {dayjs(subscriptionDetails?.next_payment_attempt).format(
                  "DD MMMM, YYYY",
                )}
              </Typography>
              .
            </Typography>
          }
        />
      )}

      {/* Stats Cards Grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns:
            subscriptionDetails.pricing_type === "tiered"
              ? "repeat(3, 1fr)"
              : "repeat(4, 1fr)",
          gap: "8px",
          backgroundColor: theme.palette.neutral["005"],
          padding: "8px",
        }}
      >
        {/* Seats Card */}
        <Stack
          sx={{
            p: "24px",
            borderRadius: "8px",
            borderColor: theme.palette.neutral["015"],
            backgroundColor: theme.palette.neutral.main,
            boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.08)",
            gap: "16px",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap="16px"
            sx={{ maxHeight: "24px" }}
          >
            <Stack direction="row" alignItems="center" gap="8px">
              <SeatIcon />
              <Typography variant="h6">Seats</Typography>
            </Stack>
            {subscriptionDetails.pricing_type !== "tiered" && (
              <Button
                variant="ghost"
                isLink
                size="small"
                onClick={() => setIsManageSeatsDrawerOpen(true)}
              >
                Manage
              </Button>
            )}
          </Stack>
          <Typography variant="h2" sx={{ color: theme.palette.lightBg.high }}>
            {subscriptionDetails?.seat_quantity || 0}
          </Typography>
          {/* <Typography variant="body-medium" color={theme.palette.lightBg.low}>
            ${pricePerSeat.toFixed(0)} per seat
          </Typography> */}
        </Stack>

        {/* Technicians Card */}
        <Stack
          sx={{
            p: "24px",
            borderRadius: "8px",
            borderColor: theme.palette.neutral["015"],
            backgroundColor: theme.palette.neutral.main,
            boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.08)",
            gap: "16px",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap="16px"
            sx={{ maxHeight: "24px" }}
          >
            <Stack direction="row" alignItems="center" gap="8px">
              <HeadphoneIcon />
              <Typography variant="h6">Team members</Typography>
            </Stack>
            {subscriptionDetails.pricing_type !== "tiered" && (
              <Button
                variant="ghost"
                isLink
                size="small"
                onClick={() => setIsManageTechDrawerOpen(true)}
              >
                Manage
              </Button>
            )}
          </Stack>
          <Typography variant="h2" sx={{ color: theme.palette.lightBg.high }}>
            {activeResourceCount || 0}{" "}
            <Typography
              component={"span"}
              variant="body-medium"
              color={theme.palette.lightBg.low}
            >
              active
            </Typography>
          </Typography>
          {/* <Typography variant="body-medium" color={theme.palette.lightBg.low}>
            active of {subscriptionDetails?.seat_quantity || 0} technicians
          </Typography> */}
        </Stack>

        {/* Next Billing Date Card */}
        <Stack
          sx={{
            p: "24px",
            borderRadius: "8px",
            borderColor: theme.palette.neutral["015"],
            backgroundColor: theme.palette.neutral.main,
            boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.08)",
            gap: "16px",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            gap="8px"
            sx={{ maxHeight: "24px" }}
          >
            <CalendarIcon
              sx={{ color: theme.palette.lightBg.low, fontSize: "20px" }}
            />
            <Typography variant="h6">Next billing date</Typography>
          </Stack>
          <Typography variant="h2" sx={{ color: theme.palette.lightBg.high }}>
            {dayjs(subscriptionDetails?.current_period_end).format(
              "DD MMM YYYY",
            )}
          </Typography>
        </Stack>

        {/* Total Billing Amount Card */}
        {subscriptionDetails.pricing_type !== "tiered" && (
          <Stack
            sx={{
              p: "24px",
              borderRadius: "8px",
              borderColor: theme.palette.neutral["015"],
              backgroundColor: theme.palette.neutral.main,
              boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.08)",
              gap: "16px",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              gap="8px"
              sx={{ maxHeight: "24px" }}
            >
              <DollarCashIcon
                sx={{ color: theme.palette.lightBg.low, fontSize: "20px" }}
              />
              <Typography variant="h6">Upcoming charge</Typography>
            </Stack>
            <Typography variant="h2" sx={{ color: theme.palette.lightBg.high }}>
              ${totalBillingAmount.toFixed(2)}
            </Typography>
          </Stack>
        )}
      </Box>
      {/* Bottom Section Grid - Address and Payment */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "8px",
          px: "8px",
        }}
      >
        {/* Customer Address Details Card */}
        <Paper
          variant="outlined"
          sx={{
            p: "24px",
            borderRadius: "8px",
            borderColor: theme.palette.neutral["015"],
            backgroundColor: theme.palette.neutral.main,
          }}
        >
          <Stack direction="row" alignItems="center" gap="8px" mb="8px">
            <LocationPinIcon fontSize="small" />
            <Typography variant="h6">Billing address</Typography>
          </Stack>
          <Typography
            variant="body-medium"
            sx={{ color: theme.palette.lightBg.low, mb: "16px" }}
          >
            Update the billing address used for your DeskDay invoices and
            payments.
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: "16px",
              borderRadius: "8px",
              backgroundColor: theme.palette.neutral["B-002"],
              borderColor: theme.palette.neutral["B-015"],
              minHeight: "216px",
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
            >
              <Box>
                {/* <Typography variant="subtitle1" sx={{ mb: "4px" }}>
                  {subscriptionDetails?.company_name || "Company Name"}
                </Typography> */}
                {getAddressParts().length > 0 ? (
                  getAddressParts().map((part, index) => (
                    <Typography
                      // biome-ignore lint/suspicious/noArrayIndexKey: parts items are ordered
                      key={index}
                      variant="body-medium"
                      color={theme.palette.lightBg.low}
                    >
                      {part}
                    </Typography>
                  ))
                ) : (
                  <Typography
                    variant="body-medium"
                    color={theme.palette.lightBg.low}
                  >
                    No address on file
                  </Typography>
                )}
              </Box>
              <Button
                variant="ghost"
                size="small"
                isLink
                onClick={() => setIsAddressEditDrawerOpen(true)}
              >
                Edit
              </Button>
            </Stack>
          </Paper>
        </Paper>

        {/* Payment Method Card */}
        <Paper
          variant="outlined"
          sx={{
            p: "24px",
            borderRadius: "8px",
            borderColor: theme.palette.neutral["015"],
            backgroundColor: theme.palette.neutral.main,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb="8px"
          >
            <Stack direction="row" alignItems="center" gap="8px">
              <CreditCardIcon />
              <Typography variant="h6">Payment method</Typography>
            </Stack>
            <Button
              variant="ghost"
              size="small"
              onClick={() => setIsCardListDrawerOpen(true)}
            >
              View all
            </Button>
          </Stack>
          <Typography
            variant="body-medium"
            sx={{ color: theme.palette.lightBg.low, mb: "16px" }}
          >
            Manage the payment methods used for your DeskDay subscription.
          </Typography>

          {/* Payment Cards List */}
          <Stack gap="12px">
            {cardList
              ?.toSorted((a: SubscriptionCard, b: SubscriptionCard) => {
                const aIsDefault = a.id === subscriptionDetails?.source;
                const bIsDefault = b.id === subscriptionDetails?.source;
                if (aIsDefault && !bIsDefault) return -1;
                if (!aIsDefault && bIsDefault) return 1;
                return 0;
              })
              .slice(0, 2)
              .map((card: SubscriptionCard, index: number) => {
                const isDefault =
                  card.id === subscriptionDetails?.source ||
                  (cardList.length === 1 && index === 0);
                return (
                  <Paper
                    key={card.id}
                    variant="outlined"
                    sx={{
                      p: "16px 24px",
                      borderRadius: "8px",
                      borderColor: isDefault
                        ? theme.palette.primary.main
                        : theme.palette.neutral["015"],
                      backgroundColor: isDefault
                        ? theme.palette.neutral["B-002"]
                        : theme.palette.neutral.main,
                      cursor: "pointer",
                      "&:hover": {
                        borderColor: isDefault
                          ? theme.palette.primary.main
                          : theme.palette.neutral["020"],
                      },
                    }}
                    onClick={() => !isDefault && handleSetAsDefault(card.id)}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Stack direction="row" alignItems="center" gap="16px">
                        <Stack>
                          <Typography variant="caption-2">
                            **** **** **** {card?.card?.last4}
                          </Typography>
                          <Typography
                            variant="body-small"
                            color={theme.palette.lightBg.medium}
                            sx={{ textTransform: "capitalize" }}
                          >
                            {card?.card?.brand} Card &bull; Expiry date :{" "}
                            {card?.card?.exp_month}/{card?.card?.exp_year}
                          </Typography>
                        </Stack>
                      </Stack>
                      <Stack direction="row" alignItems="center" gap="12px">
                        {isDefault && (
                          <Chip
                            label="PRIMARY CARD"
                            size="small"
                            sx={{
                              ...theme.typography["tag-label-xs"],
                              backgroundColor:
                                theme.palette.primitive.system.violet[50],
                              color: theme.palette.primitive.system.violet[800],
                              borderRadius: "4px",
                              height: "20px",
                              textTransform: "uppercase",
                            }}
                          />
                        )}
                        <Box
                          component="img"
                          src={cardIcon(card?.card?.brand)}
                          sx={{ width: "40px", height: "auto" }}
                        />
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })}

            <Button
              variant="secondary"
              onClick={handleAddCardClick}
              loading={loadingSetupIntent}
              sx={{ mt: "8px", ml: "auto" }}
            >
              Add payment method
            </Button>
          </Stack>
        </Paper>
      </Box>
      {subscriptionDetails.pricing_type !== "tiered" && (
        <Box
          sx={{
            p: "8px",
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              p: "24px",
              borderRadius: "8px",
              borderColor: theme.palette.neutral["015"],
              backgroundColor: theme.palette.neutral.main,
            }}
          >
            <Button
              variant="secondary"
              onClick={() => setIsCancelSubscriptionModalOpen(true)}
            >
              Cancel subscription
            </Button>
          </Paper>
        </Box>
      )}

      {/* Manage Seats Drawer */}
      <ManageSeatsDrawer
        type={"EDIT"}
        open={isManageSeatsDrawerOpen}
        onClose={() => setIsManageSeatsDrawerOpen(false)}
        subscriptionDetails={subscriptionDetails}
      />

      {/* Manage Technicians Drawer */}
      <ManageTechDrawer
        open={isManageTechDrawerOpen}
        onClose={() => setIsManageTechDrawerOpen(false)}
      />

      {/* Address Edit Drawer */}
      <AddressEditDrawer
        open={isAddressEditDrawerOpen}
        onClose={() => setIsAddressEditDrawerOpen(false)}
        subscriptionDetails={subscriptionDetails}
        onSuccess={refetchSubscriptionDetails}
      />
      {/* Add Card Modal */}
      {isAddCardsOpen && setupIntentId && (
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret: setupIntentId,
          }}
        >
          <AddPaymentMethod
            open={isAddCardsOpen}
            onClose={() => setIsAddCardsOpen(false)}
            onPaymentAdded={(id) => {
              handleSetAsDefault(id);
            }}
          />
        </Elements>
      )}

      {/* Card List Drawer */}
      <CardListDrawer
        open={isCardListDrawerOpen}
        onClose={() => setIsCardListDrawerOpen(false)}
        cardList={cardList}
        currentSourceId={subscriptionDetails?.source}
        onCardUpdated={handleCardUpdated}
      />

      {/* Invoices Drawer */}
      <InvoicesDrawer
        open={isInvoicesDrawerOpen}
        onClose={() => setIsInvoicesDrawerOpen(false)}
      />

      {/* Cancel Subscription Drawer */}
      {isCancelSubscriptionModalOpen && (
        <CancelSubscriptionFeedbackDrawer
          currentAccessBlockDate={dayjs(currentAccessBlockDate).format(
            dateFormat,
          )}
          onClose={() => setIsCancelSubscriptionModalOpen(false)}
          open={isCancelSubscriptionModalOpen}
          onConfirmClick={() => {
            refetchSubscriptionDetails();
            window.location.reload();
          }}
        />
      )}
    </Box>
  );
};
